import React, { useState, useRef, useEffect } from 'react';
import { connect } from "react-redux";

import axios from "axios";
import { isEqual } from "lodash";
import { useLazyQuery } from '@apollo/client';

import { GET_VEHICLE } from '../../../../../GraphQL/Queries/vehicles';

import { setVehicleDb } from "../../../../../Actions/dataActions";
import { setOverlay, closePullout, setCurrentVehicle } from "../../../../../Actions/stakeholderActions";
import { checkCredentials } from '../../../../../Helpers/checkCredentials';
import { LoadingIcon } from '../../../../Common/Loading_Icon';

import { VehicleDetails } from './VehicleDetails';
import VehicleForm from './VehicleForm';

// This component was a 2000+ line file. I split it into components with their own smaller components (VehicleDetails and VehicleForm).
// I think it could be a lot cleaner if we handle this logic in some kind os "local" state as a context or something to avoid passing so many props down.
// As we didn't have time to do that, I just split the file into smaller components and passed the props down.
// TODO: Handle this logic in some contexts or something to avoid passing so many props and grouping related logic together.

const VehicleOverlay = (props) => {
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [containerHeight, setContainerHeight] = useState(48);

    const [vehicle, setVehicle] = useState(null);

    const [isCreatingVehicle, setIsCreatingVehicle] = useState(false);
    const [isEditingVehicle, setIsEditingVehicle] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [legacyApiVehicleData, setLegacyApiVehicleData] = useState(null);

    const [rr_vehicle, setRrVehicle] = useState(0);
    const [vehicle_usage_types, setVehicleUsageTypes] = useState(null);
    const [vehicle_options, setVehicleOptions] = useState(null);
    const [vehicle_oem_details, setVehicleOemDetails] = useState(null);

    // overlay ref
    const contentRef = useRef();
    const overlayRef = useRef();

    // file upload refs
    const fileInputRef = useRef();
    const fileDropRef = useRef();
    const specInputRef = useRef();
    const specDropRef = useRef();

    // input refs
    const interiorColourRef = useRef();
    const exteriorColourRef = useRef();
    const warrantyDateRef = useRef();
    const availableDateRef = useRef();
    const retireDateRef = useRef();

    const inputRefs = { interiorColourRef, exteriorColourRef, warrantyDateRef, availableDateRef, retireDateRef };

    const closeModal = () => {
        window.history.back();
    }

    const handleResize = () => {
        if (contentRef.current) {
            const docHeight = contentRef.current.offsetHeight + 48;
            if (docHeight) {
                setWindowWidth(window.innerWidth);
                setContainerHeight(docHeight);
                return;
            }
        }

        setWindowWidth(window.innerWidth);
        setContainerHeight(48);
    }


    const [getVehicleQuery, { loading: loadingVehicle, error, data, refetch: refetchVehicle }] = useLazyQuery(GET_VEHICLE, {
        variables: { id: +props.match.params.id },
        errorPolicy: 'all'
    });

	useEffect(() => {
		if (data?.getVehicle) {
            const parsedVehicle = { ...legacyApiVehicleData?.vehicle, ...data.getVehicle };
			setVehicle(parsedVehicle);
            props.dispatch(setCurrentVehicle(parsedVehicle));
		}
	}, [data]);

	useEffect(() => {
		if (vehicle) {
			refetchVehicle({ id: +props.match.params.id }).then(response => {
				setVehicle({ ...legacyApiVehicleData?.vehicle, ...response?.data.getVehicle });
			});
		}
	}, [legacyApiVehicleData]);

    useEffect(() => {
        window.addEventListener("resize", handleResize);

        //Check if the details of the car are in the database just in case
        props.dispatch(closePullout())

        const { url, params } = props.match;
        const currentPath = url.split("/");
        const lastPathParam = currentPath[currentPath.length - 1];
        const creatingVehicle = lastPathParam === "New";
        const editingVehicle = params?.tab === "Edit";

        setIsCreatingVehicle(creatingVehicle);
        setIsEditingVehicle(editingVehicle);

        if (!creatingVehicle) {
            getVehicleData();
        }

        return () => {
            window.removeEventListener("resize", handleResize);
        }
    }, []);

    useEffect(() => {
        const shouldUpdateVehicleData = legacyApiVehicleData && props.stakeholder.current_vehicle_details && !isEqual(legacyApiVehicleData, props.stakeholder.current_vehicle_details);
        if (shouldUpdateVehicleData) {
            setLegacyApiVehicleData(props.stakeholder.current_vehicle_details);
        }
    }, [props.stakeholder.current_vehicle_details]);

    useEffect(() => {
        if (props.settings) {
            setVehicleOptions(props.settings.vehicle_options);
            setVehicleOemDetails(props.settings.vehicle_details);
        }
    }, [props.settings]);

    const editVehicle = () => {
		const { v_id, id } = props.match.params;
		const [basePath] = (props.match.url).split(`/${v_id}`);
		const redirectTo = `${basePath}/${v_id}/Edit/${id}`;

        setIsEditingVehicle(true);

		props.history.push(redirectTo);
    }

    const closeForm = (refetchData = false) => {
        if (refetchData) {
            getVehicleData(); // it will update legacy api data and then trigger the gql query refetch
        }

        setIsCreatingVehicle(false);
        setIsEditingVehicle(false);

        props.history.goBack();
    }

    const getVehicleData = () => {
        getVehicleQuery();

        axios.get('/ajax/vehicle/' + props.match.params.v_id)
            .then(function(response) {
                const { vehicle } = response?.data;

                const { Year, Make, Model, Trim } = vehicle;
                document.title = `${Year} ${Make} ${Model} ${Trim} - FleetXpress`;

                checkCredentials(response);

                // TODO: Try to remove the dispatches and check if the errors are still happening
                props.dispatch(setVehicleDb([vehicle], props.data.vehicle_db))
                props.dispatch(setOverlay("SET_CURRENT_VEHICLE_DETAILS", response?.data))

                setRrVehicle(vehicle.Type);
                setVehicleOptions(response?.data.vehicle_options || props.settings.vehicle_options);
                setVehicleOemDetails(response?.data.vehicle_details || props.settings.vehicle_details);
                setVehicleUsageTypes(response?.data.vehicle_usage_types);
                setLegacyApiVehicleData(response?.data);

                setEditingVehicle(vehicle);
            })
            .catch(function(error) {
                console.error('get vehicle error :>> ', error);

                if(error.response?.status == 401){
                    showStatusMsg(error.response?.data.error);
                } else if(error.response?.status == 422) {
                    var error_message = "";

                    $.each(error.response?.data.errors, function (index, element) {
                        error_message += element + '<br />';
                    });

                    showStatusMsg(error_message);
                } else {
                    showStatusMsg(`Error occurred while fetching vehicle: ${props.match.params.v_id}`);
                }

                if (props.closeModal) props.closeModal();
            })
    }

    const toInputRef = (refName) => {
        const inputRef = inputRefs[refName];

        setTimeout(()=>{
            if (inputRef?.current) {
                inputRef.current.focus();
            }
        }, 0)
    }

    if (isCreatingVehicle || (isEditingVehicle && legacyApiVehicleData)) {
        return (
            <VehicleForm
                {...props}

            	{...{ vehicle, loadingVehicle }}

                {...{ overlayRef, contentRef, fileInputRef, fileDropRef, specInputRef, specDropRef, inputRefs }}

                {...{ editingVehicle, closeForm, isCreatingVehicle, windowWidth, containerHeight, vehicle_oem_details, vehicle_options }}
            />
        )
    }

    if (legacyApiVehicleData) {
        return (
            <VehicleDetails
                {...props} props={props}

            	{...{ vehicle, loadingVehicle }}

                {...{ editVehicle, overlayRef, toInputRef, windowWidth, vehicle_usage_types, rr_vehicle, vehicle_oem_details, vehicle_options, legacyApiVehicleData }}

                closeModal={closeModal}
            />
        )
    }

    return (
        <LoadingIcon />
    )
}

const mapStateToProps = store => ({
    userSession: store.userSession,
    data: store.data,
    settings: store.settings,
    stakeholder: store.stakeholder
})

export default connect(mapStateToProps, null, null, {forwardRef: true})(VehicleOverlay)